// @ts-strict-ignore
import {
	AfterContentInit,
	AfterViewInit,
	Component,
	ContentChildren,
	input,
	Input,
	OnInit,
	output,
	QueryList,
	Self,
	TemplateRef,
	ViewChild,
} from "@angular/core";
import {SelectionModel} from "@angular/cdk/collections";
import {MatSort, SortDirection} from "@angular/material/sort";
import {MatPaginator} from "@angular/material/paginator";
import {MatColumnDef, MatTable} from "@angular/material/table";
import {ActivatedRoute, Router} from "@angular/router";
import {startWith} from "rxjs/operators";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";
import {merge} from "rxjs";
import {zoomInOnEnterAnimation, zoomOutOnLeaveAnimation} from "angular-animations";
import {animate, state, style, transition, trigger} from "@angular/animations";
import {Translations} from "@translations";
import {BulkAction, BulkActionOutput, LoadingDataParams} from "../basic-administration-table-page/administration-table-page.types";
import {SimMaterialDialogService, SimSystemService, TrackByMethods} from "@services";
import {FontAwesomeCode, IId, SearchableColumn} from "@models";
import {MatMenu} from "@angular/material/menu";
import {TableColumnSearchComponent} from "../table-column-search/table-column-search.component";
import {CdkDragDrop, moveItemInArray} from "@angular/cdk/drag-drop";
import {FormControl} from "@angular/forms";
import {difference} from "lodash-es";

export interface ActionConfigSimSettingsTable {
	isDeletable: "true" | "false" | "disabled";
	isEditable: "true" | "false" | "disabled";
	isDuplicatable: "true" | "false" | "disabled";
	isJSONViewable: "true" | "false" | "disabled";
	isSearchable: "true" | "false" | "disabled";
	supportMultiCreate: boolean;
	hideCreate?: boolean;
	hideSelection?: boolean;
	hideEditAll?: boolean;
}

export interface DeleteDialogConfig {
	title: string;
	text: string;
}

@UntilDestroy()
@Component({
	selector: "sim-basic-admin-settings-table",
	templateUrl: "./basic-admin-settings-table.component.html",
	styleUrls: ["./basic-admin-settings-table.component.scss"],
	animations: [
		zoomInOnEnterAnimation({duration: 175}),
		zoomOutOnLeaveAnimation({duration: 175}),
		trigger("detailExpand", [
			state("collapsed", style({height: "0px", minHeight: "0"})),
			state("expanded", style({height: "*"})),
			transition("expanded <=> collapsed", animate("225ms cubic-bezier(0.4, 0.0, 0.2, 1)")),
		]),
	],
	standalone: false,
})
export class BasicAdminSettingsTableComponent<T extends IId> implements OnInit, AfterViewInit, AfterContentInit {
	TrackByMethods = TrackByMethods;
	T = Translations;

	// Inputs
	@Input() expandedElement: T | null;
	@Input() currentPageIndex: number = 0;
	@Input() currentPageSize: number = 0;
	@Input() busyPromises: Promise<any> | Promise<any>[];
	@Input() title: string;
	@Input() titleIcon: string;
	@Input() isLoadingResults: boolean;
	@Input() resultsLength: number;
	@Input() showBackButton: boolean;
	@Input() changeOrders: boolean = false;
	@Input() displayedColumns: string[];
	@Input() dataSource: T[];
	@Input() selection: SelectionModel<T>;
	@Input() defaultSortColumn: string;
	@Input() sortDisableClear: boolean = true;
	@Input() defaultSortDirection: SortDirection = "asc";
	@Input() actionConfig: ActionConfigSimSettingsTable = {
		isDeletable: "disabled",
		isDuplicatable: "disabled",
		isEditable: "disabled",
		isJSONViewable: "disabled",
		isSearchable: "disabled",
		supportMultiCreate: false,
	};
	@Input() deleteDialogConfig: DeleteDialogConfig = {
		title: "Delete Item",
		text: "Do you want to delete this item?",
	};
	@Input() deleteElementText: string;
	@Input() searchFilterColumn: keyof T & string;
	@Input() enableExpandElement: boolean = false;
	@Input() enablePagination: boolean = true;
	@Input() hasAdditionalActions = false;
	@Input() editAllActive: boolean;
	@Input() currentEditElement: T = null;
	@Input() translatedHeader: string;
	@Input() headerIcon: FontAwesomeCode;
	@Input() searchableColumns: SearchableColumn[] = [];
	@Input() additionalActionsTemplateRef: TemplateRef<any>;
	backRoute = input<string | null>(null);

	// Outputs
	loadData = output<LoadingDataParams>();
	editElementClicked = output<T>();
	duplicateElementClicked = output<T>();
	downloadElementClicked = output<T>();
	deleteElementClickedEmitter = output<T>();
	bulkActionClicked = output<BulkActionOutput<T>>();
	createElementClicked = output<void>();
	createMultiElementClicked = output<void>();
	expandElementClicked = output<T>();
	editAllClickedEmitter = output<void>();
	saveAllClickedEmitter = output<boolean>();
	removeDraftItemClicked = output<void>();
	duplicateItemClickedEmitter = output<T>();
	viewElementAsJSONClicked = output<T>();
	dataSourceChanged = output<T[]>();

	// View Children
	@ViewChild(MatPaginator) paginator: MatPaginator;
	@ViewChild("additionalActionsMenue", {static: false}) additionalActionsMenue: MatMenu;
	@ViewChild(MatTable, {static: true}) table: MatTable<any>;
	@ViewChild(TableColumnSearchComponent, {static: true}) public tableColumnSearchComponent: TableColumnSearchComponent;
	@ContentChildren(MatColumnDef) columnDefs: QueryList<MatColumnDef>;

	// Properties
	public selectedBulkAction: BulkAction;
	public expandedElementIndex: number = -1;
	public filterFieldShowing: boolean = false;
	public searchFilterValue: string;
	public columnCtrl = new FormControl<string[]>([]);
	public filteredColumns: string[] = [];
	public defaultColumns = [];
	protected bulkActions: BulkAction[] = [
		{
			value: "delete",
			displayNameTranslationKey: "delete",
			methodName: "bulkDelete",
		},
	];
	//Query Params
	private _query = {
		pageSize: 10,
		pageIndex: 0,
		sortActiveColumn: "",
		sortDirection: "" as SortDirection,
		searchFilter: "",
		searchFilterColumn: "" as keyof T & string,
	};
	private _queryParamsLoaded = false;

	constructor(
		@Self() private _matSort: MatSort,
		private _router: Router,
		private _activatedRoute: ActivatedRoute,
		private _dialogService: SimMaterialDialogService,
		private _simSystemService: SimSystemService,
	) {
		this._activatedRoute.queryParams.pipe(untilDestroyed(this)).subscribe((params) => {
			const pageIndex = parseInt(params["pageIndex"]);
			const sortActiveColumn = params["sortActiveColumn"];
			const sortDirection = params["sortDirection"];
			const pageSize = parseInt(params["pageSize"]);
			const searchFilter = params["searchFilter"];
			const searchFilterColumn = params["searchFilterColumn"];

			if (pageIndex) {
				this._query.pageIndex = pageIndex;
			}

			if (sortActiveColumn && sortActiveColumn?.length > 0) {
				this._query.sortActiveColumn = sortActiveColumn;
			}

			if (sortDirection && sortDirection?.length > 0) {
				this._query.sortDirection = sortDirection;
			}

			if (pageSize && pageSize > 0) {
				this._query.pageSize = pageSize;
			}

			if (searchFilterColumn && searchFilterColumn?.length > 0) {
				this._query.searchFilterColumn = searchFilterColumn;
			}

			if (searchFilter && searchFilter?.length > 0) {
				this._query.searchFilter = searchFilter;
				this.filterFieldShowing = true;
			}

			this._queryParamsLoaded = true;
		});
	}

	@Input() getClassForSpecificRow: (element: T) => string = (element: T) => {
		return "";
	};

	public ngOnInit(): void {
		// Check for default columns
		this.defaultColumns = this.displayedColumns.filter((col) => col === "select" || col === "actions");
		// Find localStorageKey
		const localStorageKey = this.translatedHeader.replace(" ", "");

		// Observe which columns to display
		this.columnCtrl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
			this.filteredColumns = [
				...(this.defaultColumns.includes("select") ? ["select"] : []),
				...value,
				...(this.defaultColumns.includes("actions") ? ["actions"] : []),
			];

			// Save the selected columns to localStorage
			window.localStorage.setItem(localStorageKey, JSON.stringify(value));
		});

		// Load the selected columns from localStorage on init
		const storedColumnsJson = window.localStorage.getItem(localStorageKey);
		if (storedColumnsJson) {
			const storedColumns = JSON.parse(storedColumnsJson) as string[];
			const storedNonExistent = storedColumns.filter((col) => !this.displayedColumns.includes(col));
			if (storedNonExistent.length > 0) {
				// remove the non-existent columns from the selection
				this.columnCtrl.setValue(difference(storedColumns, storedNonExistent));
			} else {
				this.columnCtrl.setValue(storedColumns);
			}
		} else {
			this.columnCtrl.setValue(this.displayedColumns.filter((column) => !this.defaultColumns.includes(column)));
		}
	}

	resetColumns() {
		this.columnCtrl.setValue(this.displayedColumns.filter((column) => !this.defaultColumns.includes(column)));
	}

	ngAfterContentInit() {
		this.columnDefs.forEach((columnDef) => this.table.addColumnDef(columnDef));
		if (!this.searchableColumns.length) {
			this.searchableColumns = this.columnDefs.map((c) => ({
				value: c.name,
				displayName: c.name,
				type: "FreeText",
			}));
		}

		if (!!this._query.searchFilterColumn) {
			this.searchFilterColumn = this._query.searchFilterColumn;
		} else if (!this.searchFilterColumn) {
			this.searchFilterColumn = this.searchableColumns?.[0]?.value as any;
		}

		if (!!this._query.searchFilter) {
			this.searchFilterValue = this._query.searchFilter;
		}

		let sortActiveColumn = this._query.sortActiveColumn;
		if (!sortActiveColumn && this.defaultSortColumn) {
			sortActiveColumn = this.defaultSortColumn;
		}

		let sortDirection = this._query.sortDirection;
		if (!sortDirection && this.defaultSortDirection) {
			sortDirection = this.defaultSortDirection;
		}

		this._matSort.sort({
			id: sortActiveColumn,
			start: sortDirection,
			disableClear: this.sortDisableClear,
		});
	}

	dropTable(event: CdkDragDrop<any[]>): void {
		if (this.changeOrders) {
			moveItemInArray(this.dataSource, event.previousIndex, event.currentIndex);
			this.updateOrderProperty();
			this.emitDataSource();
			this.table.renderRows();
		}
	}

	/** Selects all rows if they are not all selected; otherwise clear selection. */
	masterToggle() {
		if (this.isAllSelected()) {
			this.selection?.clear();
			return;
		}

		this.selection?.select(...this.dataSource);
	}

	isAllSelected() {
		const numSelected = this.selection?.selected.length;
		const numRows = this.dataSource.length;
		return numSelected === numRows;
	}

	ngAfterViewInit(): void {
		// If the user changes the sort order, reset back to the first page.
		this._matSort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

		merge(this._matSort.sortChange, this.paginator.page)
			.pipe(startWith({}))
			.subscribe((data) => {
				this.currentPageIndex = this.paginator.pageIndex;
				this.emitLoadData();
				this.selection?.clear();
			});
	}

	openFilterField() {
		this.filterFieldShowing = !this.filterFieldShowing;

		if (this.filterFieldShowing) {
			this.tableColumnSearchComponent.focus();
		} else {
			this.searchFilterValue = "";
		}
	}

	handleSearchFilterValueChange(value: string) {
		this.searchFilterValue = value;
		this.emitLoadData();
	}

	executeBulkAction() {
		this.bulkActionClicked.emit({
			type: this.selectedBulkAction,
			values: this.selection?.selected,
		});
	}

	notifyExpandedElementChanged(element: T, index: number) {
		if (this.enableExpandElement) {
			this.expandedElement = this.expandedElement === element ? null : element;

			if (this.expandedElement) {
				this.expandedElementIndex = index;
			} else {
				this.expandedElementIndex = -1;
			}

			this.expandElementClicked.emit(this.expandedElement);
		}
	}

	editToggleClicked(element: T) {
		setTimeout(() => {
			if (this.currentEditElement === element) {
				// save was clicked explicitly
				this.editElementClicked.emit(null);
			} else {
				// another element was clicked, so save the current one
				this.editElementClicked.emit(element);
			}
		}, 200);
	}

	openJSONViewDialog(element: T) {
		this.viewElementAsJSONClicked.emit(element);
		this._dialogService.openJSONViewDialog({
			elementToShow: element,
		});
	}

	editAllClicked() {
		this.editAllClickedEmitter.emit();
	}

	creatElementClicked() {
		this.createElementClicked.emit();

		document.getElementById("contentTableHolder").scrollTo({
			top: 0,
			left: 0,
			behavior: "smooth",
		});
	}

	public deleteElementClicked(element: T) {
		this._dialogService
			.openDeleteDialog({
				deleteTitle: this.deleteDialogConfig.title,
				deleteText: this.deleteDialogConfig.text,
			})
			.then((result) => {
				if (result.action === "ok") {
					this.deleteElementClickedEmitter.emit(element);
				}
			});
	}

	createMultiElement() {
		if (this.currentEditElement) {
			this.editToggleClicked(this.currentEditElement);
		}

		this.createMultiElementClicked.emit();

		document.getElementById("contentTableHolder").scrollTo({
			top: 0,
			left: 0,
			behavior: "smooth",
		});
	}

	updateOrderNumbersByModel(element: T) {
		this._simSystemService.updateOrderByObjectModel({id: element.id, order: element["order"]}).then((success) => {});
	}

	private updateOrderProperty(): void {
		if (this.changeOrders) {
			this.dataSource.forEach((element, index) => {
				if (element["order"] != index + 1) {
					element["order"] = index + 1;
					this.updateOrderNumbersByModel(element);
				}
			});
		}
	}

	private emitDataSource(): void {
		if (this.changeOrders) {
			// Emit the updated dataSource as an output
			this.dataSourceChanged.emit([...this.dataSource]);
		}
	}

	private emitLoadData() {
		this.loadData.emit({
			searchTerm: this.searchFilterValue,
			pageIndex: this.enablePagination ? this.paginator.pageIndex : 0,
			pageSize: this.enablePagination ? this.paginator.pageSize : 10000,
			sortDirection: this._matSort.direction,
			sortActiveColumn: this._matSort.active,
			searchColumn: this.searchFilterColumn,
		});
		this.adaptQueryParams();
	}

	private adaptQueryParams() {
		if (!this._queryParamsLoaded) return;

		console.log("Adapt Query Params");
		console.log(this._router.url);

		this._router.navigate([], {
			relativeTo: this._activatedRoute,
			queryParams: {
				pageSize: this.paginator.pageSize,
				pageIndex: this.paginator.pageIndex,
				sortActiveColumn: this._matSort.active,
				sortDirection: this._matSort.direction === "" ? "asc" : this._matSort.direction,
				searchFilter: this.searchFilterValue || null,
				searchFilterColumn: this.searchFilterColumn,
			},
			queryParamsHandling: "merge", // remove to replace all query params by provided
		});
	}

	protected navigateBack() {
		this._router.navigate([this.backRoute()]).then();
	}
}
